<template>
  <div class="p-6">
    <div class="mb-6">
      <button
        @click="$router.push({ name: 'new-roles' })"
        class="text-indigo-600 hover:text-indigo-800 font-medium"
      >
        ← Back to Role Comparison
      </button>
    </div>

    <h1 class="text-2xl font-bold text-gray-800 mb-6">CSV Upload</h1>

    <!-- Alert Messages -->
    <div v-if="successMessage" class="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
      {{ successMessage }}
    </div>
    <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
      {{ errorMessage }}
    </div>

    <div class="space-y-6">
      <!-- New Roles CSV Upload -->
      <div class="bg-white p-6 rounded-lg border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">New Roles CSV</h2>
            <p class="text-sm text-gray-600 mt-1">Upload new_roles.csv file</p>
            <p v-if="newRolesLastUploaded" class="text-xs text-gray-500 mt-1">
              Last uploaded: {{ formatDate(newRolesLastUploaded) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-4">
          <input
            ref="newRolesInput"
            type="file"
            accept=".csv"
            @change="handleNewRolesFileSelect"
            class="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-medium
              file:bg-indigo-50 file:text-indigo-700
              hover:file:bg-indigo-100"
          />
          <button
            @click="uploadNewRoles"
            :disabled="!newRolesFile || uploadingNewRoles"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {{ uploadingNewRoles ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
        <p v-if="newRolesFile" class="text-sm text-gray-600 mt-2">
          Selected: {{ newRolesFile.name }}
        </p>
      </div>

      <!-- Role Mapping CSV Upload -->
      <div class="bg-white p-6 rounded-lg border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Role Mapping CSV</h2>
            <p class="text-sm text-gray-600 mt-1">Upload role_mapping.csv file</p>
            <p v-if="roleMappingLastUploaded" class="text-xs text-gray-500 mt-1">
              Last uploaded: {{ formatDate(roleMappingLastUploaded) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-4">
          <input
            ref="roleMappingInput"
            type="file"
            accept=".csv"
            @change="handleRoleMappingFileSelect"
            class="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-medium
              file:bg-indigo-50 file:text-indigo-700
              hover:file:bg-indigo-100"
          />
          <button
            @click="uploadRoleMapping"
            :disabled="!roleMappingFile || uploadingRoleMapping"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {{ uploadingRoleMapping ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
        <p v-if="roleMappingFile" class="text-sm text-gray-600 mt-2">
          Selected: {{ roleMappingFile.name }}
        </p>
      </div>

      <!-- Environment Mapping CSV Upload -->
      <div class="bg-white p-6 rounded-lg border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Environment Mapping CSV</h2>
            <p class="text-sm text-gray-600 mt-1">Upload environment_mapping.csv file</p>
            <p v-if="environmentMappingLastUploaded" class="text-xs text-gray-500 mt-1">
              Last uploaded: {{ formatDate(environmentMappingLastUploaded) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-4">
          <input
            ref="environmentMappingInput"
            type="file"
            accept=".csv"
            @change="handleEnvironmentMappingFileSelect"
            class="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-medium
              file:bg-indigo-50 file:text-indigo-700
              hover:file:bg-indigo-100"
          />
          <button
            @click="uploadEnvironmentMapping"
            :disabled="!environmentMappingFile || uploadingEnvironmentMapping"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {{ uploadingEnvironmentMapping ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
        <p v-if="environmentMappingFile" class="text-sm text-gray-600 mt-2">
          Selected: {{ environmentMappingFile.name }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: "NewRolesCsvUploadView",
  data() {
    return {
      newRolesFile: null,
      roleMappingFile: null,
      environmentMappingFile: null,
      uploadingNewRoles: false,
      uploadingRoleMapping: false,
      uploadingEnvironmentMapping: false,
      successMessage: '',
      errorMessage: '',
      newRolesLastUploaded: null,
      roleMappingLastUploaded: null,
      environmentMappingLastUploaded: null
    }
  },
  async mounted() {
    await this.fetchFileInfo()
  },
  methods: {
    async fetchFileInfo() {
      try {
        const response = await axios.get('role-migration-csv/info')
        this.newRolesLastUploaded = response.data.new_roles_last_uploaded
        this.roleMappingLastUploaded = response.data.role_mapping_last_uploaded
        this.environmentMappingLastUploaded = response.data.environment_mapping_last_uploaded
      } catch (error) {
        console.error('Failed to fetch file info:', error)
      }
    },

    handleNewRolesFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        if (file.name !== 'new_roles.csv') {
          this.errorMessage = 'File must be named "new_roles.csv"'
          this.newRolesFile = null
          this.$refs.newRolesInput.value = ''
          return
        }
        this.newRolesFile = file
        this.errorMessage = ''
      }
    },

    handleRoleMappingFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        if (file.name !== 'role_mapping.csv') {
          this.errorMessage = 'File must be named "role_mapping.csv"'
          this.roleMappingFile = null
          this.$refs.roleMappingInput.value = ''
          return
        }
        this.roleMappingFile = file
        this.errorMessage = ''
      }
    },

    async uploadNewRoles() {
      if (!this.newRolesFile) return

      this.uploadingNewRoles = true
      this.successMessage = ''
      this.errorMessage = ''

      const formData = new FormData()
      formData.append('file', this.newRolesFile)
      formData.append('type', 'new_roles')

      try {
        await axios.post('role-migration-csv/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.successMessage = 'new_roles.csv uploaded successfully!'
        this.newRolesFile = null
        this.$refs.newRolesInput.value = ''
        await this.fetchFileInfo()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to upload new_roles.csv'
      } finally {
        this.uploadingNewRoles = false
      }
    },

    async uploadRoleMapping() {
      if (!this.roleMappingFile) return

      this.uploadingRoleMapping = true
      this.successMessage = ''
      this.errorMessage = ''

      const formData = new FormData()
      formData.append('file', this.roleMappingFile)
      formData.append('type', 'role_mapping')

      try {
        await axios.post('role-migration-csv/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.successMessage = 'role_mapping.csv uploaded successfully!'
        this.roleMappingFile = null
        this.$refs.roleMappingInput.value = ''
        await this.fetchFileInfo()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to upload role_mapping.csv'
      } finally {
        this.uploadingRoleMapping = false
      }
    },

    handleEnvironmentMappingFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        if (file.name !== 'environment_mapping.csv') {
          this.errorMessage = 'File must be named "environment_mapping.csv"'
          this.environmentMappingFile = null
          this.$refs.environmentMappingInput.value = ''
          return
        }
        this.environmentMappingFile = file
        this.errorMessage = ''
      }
    },

    async uploadEnvironmentMapping() {
      if (!this.environmentMappingFile) return

      this.uploadingEnvironmentMapping = true
      this.successMessage = ''
      this.errorMessage = ''

      const formData = new FormData()
      formData.append('file', this.environmentMappingFile)
      formData.append('type', 'environment_mapping')

      try {
        await axios.post('role-migration-csv/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.successMessage = 'environment_mapping.csv uploaded successfully!'
        this.environmentMappingFile = null
        this.$refs.environmentMappingInput.value = ''
        await this.fetchFileInfo()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to upload environment_mapping.csv'
      } finally {
        this.uploadingEnvironmentMapping = false
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return 'Never'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
